/**
 * Purpose: SDK-free helpers that keep PostHog application metrics low-cardinality
 * and free of personal data. Shared by the browser wrapper (`lib/posthog-metrics.ts`)
 * and the server wrapper (`lib/posthog-metrics-server.ts`).
 *
 * Inputs: request URLs and the page origin (for {@link toUrlTemplate}); metric
 * samples carrying optional attributes (for {@link scrubMetricAttributes}).
 *
 * Outputs: route templates such as `/:locale/diary/:id`, `:external`, or `null`
 * for PostHog's own ingest traffic; samples with identifier and PII keys removed.
 */
import { i18n } from '@/i18n-config'
import { PII_ATTRIBUTE_KEYS } from '@lib/posthog-logging-service'

/** Template for any request that leaves our origin — the host is still recorded separately. */
export const EXTERNAL_URL_TEMPLATE = ':external'

/** Template for a URL that cannot be parsed, so it still lands in one bounded series. */
export const INVALID_URL_TEMPLATE = ':invalid'

const LOCALES: ReadonlySet<string> = new Set(i18n.locales)

// Record ids by shape: numeric, UUID, Prisma cuid, or any long token mixing letters and digits (cuid2, hashes).
const NUMERIC_ID = /^\d+$/
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const CUID = /^c[a-z0-9]{20,32}$/i
const LONG_TOKEN = /^(?=.*\d)(?=.*[a-z])[a-z0-9_-]{20,}$/i

function isIdSegment(segment: string): boolean {
  return NUMERIC_ID.test(segment) || UUID.test(segment) || CUID.test(segment) || LONG_TOKEN.test(segment)
}

/**
 * Whether `segments[index]` is the slug of a public blog post — `/blog/<slug>`
 * or `/<locale>/blog/<slug>`. Admin blog routes are keyed by id, not slug.
 */
function isPublicBlogSlug(segments: readonly string[], index: number): boolean {
  if (segments[index - 1] !== 'blog') return false
  const blogIndex = index - 1
  return blogIndex === 0 || (blogIndex === 1 && LOCALES.has(segments[0]))
}

/**
 * Collapses a request URL into a low-cardinality route template.
 *
 * Every distinct template is its own metric series, and raw paths carry diary
 * and admin record ids, so same-origin paths replace the locale, blog slugs
 * and id-shaped segments with placeholders. Query strings and fragments are
 * always dropped. Cross-origin URLs keep no path at all.
 *
 * Returns `null` for the `/ingest` PostHog proxy so the metrics pipeline never
 * measures itself.
 */
export function toUrlTemplate(url: string, origin: string): string | null {
  let parsed: URL
  try {
    parsed = new URL(url, origin)
  } catch {
    // Unparseable input still needs a bounded template rather than the raw string.
    return INVALID_URL_TEMPLATE
  }

  // Third-party paths can embed object keys or signed tokens; keep only the fact it left our origin.
  if (parsed.origin !== new URL(origin).origin) return EXTERNAL_URL_TEMPLATE

  const segments = parsed.pathname.split('/').filter(Boolean)
  if (segments[0] === 'ingest') return null

  // Replace each variable segment with its placeholder; static segments pass through unchanged.
  const templated = segments.map((segment, index) => {
    if (index === 0 && LOCALES.has(segment)) return ':locale'
    if (isIdSegment(segment)) return ':id'
    if (isPublicBlogSlug(segments, index)) return ':slug'
    return segment
  })

  return `/${templated.join('/')}`
}

/**
 * Attribute keys that identify a person, session, or request. Logs keep
 * `userId` deliberately; metrics must not, because every distinct value
 * becomes its own series.
 */
export const METRIC_IDENTIFIER_KEYS = ['distinctId', 'userId', 'sessionId', 'requestId'] as const

const DROPPED_METRIC_ATTRIBUTE_KEYS: ReadonlySet<string> = new Set([...PII_ATTRIBUTE_KEYS, ...METRIC_IDENTIFIER_KEYS])

type MetricAttributeValue = string | number | boolean

/**
 * `beforeSend` hook for both SDKs: drops PII and identifier attributes before
 * a sample is aggregated. Returns a new sample; never drops the sample itself.
 */
export function scrubMetricAttributes<T extends { attributes?: Record<string, MetricAttributeValue> }>(
  sample: T
): T {
  if (!sample.attributes) return sample

  // Copy only the keys that may leave the process.
  const attributes = Object.fromEntries(
    Object.entries(sample.attributes).filter(([key]) => !DROPPED_METRIC_ATTRIBUTE_KEYS.has(key))
  )
  return { ...sample, attributes }
}
